import { ListboxItem, TmdxAvatar } from '@ds';
import { Controller, useFormContext } from 'react-hook-form';
import { MdOutlinePerson } from 'react-icons/md';
import { Key, useMemo, useState } from 'react';
import { CaseFormType } from '@features/forms/CaseForms/schema';
import { getFullName, sortAlphabetically } from '@/utils/helpers';
import { ListboxItemHandlersType, SearchableListbox } from './SearchableListbox';
import { NO_COORDINATOR_ID } from '@/api/apis/case';
import { TxCustomer } from '@/api/apis/customer';

type TxCustomerInputProps = {
  contactsList: TxCustomer[];
  disabled?: boolean;
  contactFieldPlaceholder: string;
  contactFieldName: keyof CaseFormType;
  onSelect?: (selected: TxCustomer | null) => void;
  onClear?: () => void;
  isClearable?: boolean;
  isCoordinator?: boolean;
  defaultValue?: TxCustomer | null;
  isOpoRequest?: boolean;
  txCenterId?: string;
};

type FullTxCustomer = TxCustomer & {
  fullName: string;
};

export const TxCustomerInput = ({
  contactsList,
  disabled,
  contactFieldPlaceholder,
  contactFieldName,
  onSelect,
  onClear,
  isClearable,
  isCoordinator,
  defaultValue,
  isOpoRequest,
  txCenterId,
}: TxCustomerInputProps) => {
  const { control, setValue } = useFormContext();
  const [searchQuery, setSearchQuery] = useState('');

  const searchedContacts: TxCustomer[] = useMemo(() => {
    return contactsList.filter((c) => {
      const contains = (str: string) => str.toLowerCase().includes(searchQuery.toLowerCase());
      const fullName = getFullName(c.firstName, c.lastName);
      return contains(fullName) || contains(c.primaryPhone ?? '') || contains(c.email ?? '');
    });
  }, [searchQuery, contactsList]);

  const sortedContacts: FullTxCustomer[] = useMemo(() => {
    const baseList = searchedContacts.map((c) => ({
      ...c,
      fullName: getFullName(c.firstName, c.lastName),
    }));

    const sorted = sortAlphabetically<FullTxCustomer>(baseList, 'fullName');

    if (isCoordinator) {
      const noCoordinator: FullTxCustomer = {
        id: NO_COORDINATOR_ID,
        fullName: 'Service without TX Center Coordinator',
        email: 'TX Center Coordinator',
        isPrimary: false,
        enabled: true,
        status: 'Active',
        photoPath: '',
        customerInTxCenters: [],
      } as unknown as FullTxCustomer;

      return [noCoordinator, ...sorted];
    }

    return sorted;
  }, [searchedContacts, isCoordinator]);

  const getButtonContent = (selectedId: string | null | undefined) => {
    if (selectedId === NO_COORDINATOR_ID && !defaultValue && (!isOpoRequest || txCenterId)) {
      return (
        <>
          <TmdxAvatar size="md" radius="md" src="" name="" />
          <span className="text-m-m text-base-5 w-[200px] truncate text-left">
            Service without TX Center Coordinator
          </span>
        </>
      );
    }

    const contact = sortedContacts.find((c) => c.id === selectedId);

    if (contact && defaultValue) {
      return (
        <>
          <TmdxAvatar size="md" radius="md" src={contact.photoPath} name={contact.fullName} />
          <span className="text-m-m text-base-5 w-[200px] truncate text-left">{contact.fullName}</span>
        </>
      );
    }

    return (
      <>
        <MdOutlinePerson size={24} />
        <div className="text-m-r truncate">{contactFieldPlaceholder}</div>
      </>
    );
  };

  const getContactItem = (item: FullTxCustomer, handlers: ListboxItemHandlersType) => {
    const isPlaceholder = item.id === NO_COORDINATOR_ID;

    return (
      <ListboxItem key={item.id} textValue={item.id} {...handlers}>
        <div className="flex items-center gap-2">
          <TmdxAvatar size="xl" radius="xl" src={item.photoPath} name={item.fullName} />
          {isPlaceholder ? (
            <div>
              <div className="text-m-m text-base-5 w-[200px] truncate text-wrap">
                Service without TX Center Coordinator
              </div>
              <div className="text-s-r text-base-20">TX Center Coordinator</div>
            </div>
          ) : (
            <div>
              <div className="text-m-m text-base-5 w-[200px] truncate text-wrap">{item.fullName}</div>
              <div className="text-s-r text-base-20">{item.email}</div>
            </div>
          )}
        </div>
      </ListboxItem>
    );
  };

  type Selection = Set<Key> | Key | Key[] | null | undefined;

  const handleSelectionChange = (
    keys: Selection,
    onChange: (val: string | null) => void,
    currentValue: string | null,
  ) => {
    let selectedId: string | undefined;

    if (!keys) selectedId = undefined;
    else if (typeof keys === 'string' || typeof keys === 'number' || typeof keys === 'symbol') {
      selectedId = String(keys);
    } else if (Array.isArray(keys)) {
      selectedId = keys.length ? String(keys[0]) : undefined;
    } else if (keys instanceof Set) {
      const [first] = Array.from(keys);
      selectedId = first !== undefined ? String(first) : undefined;
    }

    if (!selectedId && currentValue && !isClearable) {
      return;
    }

    const selectedUser = sortedContacts.find((c) => c.id === selectedId) || null;

    onChange(selectedId ?? null);

    if (isCoordinator && selectedId === NO_COORDINATOR_ID) {
      setValue('isServiceWithoutCoordinator', true);
    }

    if (onSelect) onSelect(selectedUser);
  };

  return (
    <div className="flex gap-4">
      <div className="flex-1 truncate">
        <Controller
          name={contactFieldName}
          control={control}
          defaultValue={defaultValue?.id ?? ''}
          render={({ field: { value, onChange }, fieldState: { invalid, error } }) => (
            <>
              <SearchableListbox
                disabled={disabled || !sortedContacts.length}
                invalid={invalid}
                items={sortedContacts}
                selectedKeys={value ? new Set([value]) : new Set()}
                setSearchQuery={setSearchQuery}
                onselectionchange={(keys) => handleSelectionChange(keys, onChange, value)}
                getTriggerContent={() => getButtonContent(value)}
                renderItem={(item, handlers) => getContactItem(item as FullTxCustomer, handlers)}
                isClearable={isClearable}
                onClear={onClear}
                isEmpty={!value}
              />
              {error?.message && <div className="text-danger-40 text-s-r mt-1 ml-1">{error.message}</div>}
            </>
          )}
        />
      </div>
    </div>
  );
};
